
import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { ChevronLeft, ChevronRight, Trophy, Target, Brain, Zap, Swords } from 'lucide-react';
import { 
  ASSESSMENT_SECTIONS, 
  calculateAssessmentResult, 
  getSkillLevelInfo,
  SKILL_LEVELS 
} from '@/utils/skillAssessmentUtils';
import { AssessmentResponse, AssessmentResult } from '@/types/skillAssessment';

interface SkillAssessmentQuestionnaireProps {
  onComplete: (result: AssessmentResult) => void;
  onCancel: () => void;
}

const SkillAssessmentQuestionnaire: React.FC<SkillAssessmentQuestionnaireProps> = ({
  onComplete,
  onCancel
}) => {
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [responses, setResponses] = useState<AssessmentResponse[]>([]);
  const [showIntro, setShowIntro] = useState(true);
  const [showResults, setShowResults] = useState(false);
  const [result, setResult] = useState<AssessmentResult | null>(null);

  const currentSection = ASSESSMENT_SECTIONS[currentSectionIndex];
  const currentQuestion = currentSection?.questions[currentQuestionIndex];
  const totalQuestions = ASSESSMENT_SECTIONS.reduce((total, section) => total + section.questions.length, 0);
  const answeredQuestions = responses.length;
  const progress = (answeredQuestions / totalQuestions) * 100;

  const getSectionIcon = (sectionId: string) => {
    switch (sectionId) {
      case 'experience': return <Target className="h-4 w-4" />;
      case 'technical': return <Zap className="h-4 w-4" />;
      case 'knowledge': return <Brain className="h-4 w-4" />;
      case 'competition': return <Trophy className="h-4 w-4" />;
      case 'playing_style': return <Swords className="h-4 w-4" />;
      default: return <Target className="h-4 w-4" />;
    }
  };

  const handleResponse = (value: number) => {
    const newResponse: AssessmentResponse = {
      questionId: currentQuestion.id,
      value
    };

    setResponses(prev => {
      const filtered = prev.filter(r => r.questionId !== currentQuestion.id);
      return [...filtered, newResponse];
    });

    // Auto-advance to next question
    setTimeout(() => {
      moveToNextQuestion();
    }, 300);
  };

  const moveToNextQuestion = () => {
    if (currentQuestionIndex < currentSection.questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else if (currentSectionIndex < ASSESSMENT_SECTIONS.length - 1) {
      setCurrentSectionIndex(currentSectionIndex + 1);
      setCurrentQuestionIndex(0);
    } else {
      // Assessment complete
      const assessmentResult = calculateAssessmentResult(responses);
      setResult(assessmentResult);
      setShowResults(true);
    }
  };

  const moveToPreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    } else if (currentSectionIndex > 0) {
      setCurrentSectionIndex(currentSectionIndex - 1);
      setCurrentQuestionIndex(ASSESSMENT_SECTIONS[currentSectionIndex - 1].questions.length - 1);
    }
  };

  const getCurrentResponse = () => {
    return responses.find(r => r.questionId === currentQuestion?.id);
  };

  if (showIntro) {
    return (
      <Card className="w-full mx-auto">
        <CardHeader className="text-center pb-4">
          <CardTitle className="flex items-center justify-center gap-2">
            <Trophy className="h-5 w-5 text-primary" />
            Padel Skill Assessment
          </CardTitle>
          <CardDescription>
            This comprehensive assessment will accurately determine your skill level across four key areas
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-3">
            {SKILL_LEVELS.map((level) => (
              <div key={level.level} className="p-3 border rounded-lg">
                <div className="flex items-center justify-between mb-1">
                  <Badge variant={level.level === 'expert' ? 'default' : 'secondary'}>
                    {level.title}
                  </Badge>
                  <span className="text-xs text-muted-foreground">
                    ELO: {level.eloRange}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground">{level.description}</p>
              </div>
            ))}
          </div>
          
          <div className="text-center space-y-3 pt-2">
            <p className="text-sm text-muted-foreground">
             Takes about 7 minutes • {totalQuestions} questions across {ASSESSMENT_SECTIONS.length} sections
            </p>
            
            <div className="flex gap-3 justify-center">
              <Button onClick={() => setShowIntro(false)} className="flex items-center gap-2">
                Begin Assessment
                <ChevronRight className="h-4 w-4" />
              </Button>
              <Button variant="outline" onClick={onCancel}>
                Cancel
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (showResults && result) {
    const skillInfo = getSkillLevelInfo(result.skillLevel);
    
    return (
      <Card className="w-full mx-auto">
        <CardHeader className="text-center pb-4">
          <CardTitle className="flex items-center justify-center gap-2">
            <Trophy className="h-5 w-5 text-primary" />
            Your Skill Assessment Results
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="text-center space-y-3">
            <div>
              <Badge variant="default" className="text-base px-3 py-1">
                {skillInfo.title}
              </Badge>
              <p className="text-xl font-bold mt-2">
                ELO Rating: {result.eloRating.toFixed(1)}
              </p>
              <p className="text-sm text-muted-foreground">
                {skillInfo.description}
              </p>
            </div>

            {result.needsValidation && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-xs text-amber-800">
                  <strong>Validation Required:</strong> Your first 5 matches will be used to validate and fine-tune your rating.
                </p>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <h3 className="font-semibold text-sm">Section Breakdown:</h3>
            {ASSESSMENT_SECTIONS.filter(s => s.id !== 'playing_style').map((section) => (
              <div key={section.id} className="flex items-center justify-between p-2 bg-muted rounded-lg">
                <div className="flex items-center gap-2">
                  {getSectionIcon(section.id)}
                  <span className="text-sm font-medium">{section.title}</span>
                </div>
                <Badge variant="secondary" className="text-xs">
                  {result.sectionScores[section.id as keyof typeof result.sectionScores]} pts
                </Badge>
              </div>
            ))}
          </div>

          {result.playingStyle && (
            <div className="space-y-2">
              <h3 className="font-semibold text-sm flex items-center gap-2">
                <Swords className="h-4 w-4" /> Playing Style
              </h3>
              <div className="flex flex-wrap gap-2">
                {result.playingStyle.preferred_side && (
                  <Badge variant="outline" className="text-xs">
                    Side: {result.playingStyle.preferred_side.replace('_', ' ')}
                  </Badge>
                )}
                {result.playingStyle.play_type && (
                  <Badge variant="outline" className="text-xs">
                    Type: {result.playingStyle.play_type.replace('_', '-')}
                  </Badge>
                )}
                {result.playingStyle.preferred_position && (
                  <Badge variant="outline" className="text-xs">
                    Position: {result.playingStyle.preferred_position}
                  </Badge>
                )}
                {result.playingStyle.power_control && (
                  <Badge variant="outline" className="text-xs">
                    Style: {result.playingStyle.power_control}
                  </Badge>
                )}
                {result.playingStyle.smash_proficiency && (
                  <Badge variant="outline" className="text-xs">
                    Smash: {result.playingStyle.smash_proficiency}
                  </Badge>
                )}
                {result.playingStyle.rally_tempo && (
                  <Badge variant="outline" className="text-xs">
                    Tempo: {result.playingStyle.rally_tempo}
                  </Badge>
                )}
              </div>
            </div>
          )}

          <div className="space-y-2">
            <h3 className="font-semibold text-sm">Key Characteristics:</h3>
            <ul className="text-xs space-y-1">
              {skillInfo.characteristics.slice(0, 3).map((characteristic, index) => (
                <li key={index} className="flex items-start gap-2">
                  <span className="text-primary mt-0.5">•</span>
                  {characteristic}
                </li>
              ))}
            </ul>
          </div>

          <div className="flex gap-3 justify-center pt-2">
            <Button onClick={() => onComplete(result)} className="px-6">
              Apply This Rating
            </Button>
            <Button variant="outline" onClick={onCancel}>
              Cancel
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!currentQuestion) return null;

  return (
    <Card className="w-full mx-auto">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between mb-2">
          <Badge variant="outline" className="flex items-center gap-1">
            {getSectionIcon(currentSection.id)}
            {currentSection.title}
          </Badge>
          <span className="text-xs text-muted-foreground">
            {answeredQuestions + 1} of {totalQuestions}
          </span>
        </div>
        <Progress value={progress} className="mb-3" />
        <CardTitle className="text-base">{currentQuestion.text}</CardTitle>
        <CardDescription className="text-sm">{currentSection.description}</CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-3">
        <RadioGroup
          value={getCurrentResponse()?.value?.toString() || ''}
          onValueChange={(value) => handleResponse(parseInt(value))}
        >
          {currentQuestion.options.map((option) => (
            <div key={option.value} className="flex items-center space-x-2 p-2 border rounded-lg hover:bg-muted cursor-pointer">
              <RadioGroupItem value={option.value.toString()} id={`option-${option.value}`} />
              <Label htmlFor={`option-${option.value}`} className="flex-1 cursor-pointer">
                {option.label}
                {option.description && (
                  <span className="block text-xs text-muted-foreground mt-0.5">
                    {option.description}
                  </span>
                )}
              </Label>
            </div>
          ))}
        </RadioGroup>

        <div className="flex justify-between pt-3">
          <Button
            variant="outline"
            onClick={moveToPreviousQuestion}
            disabled={currentSectionIndex === 0 && currentQuestionIndex === 0}
            className="flex items-center gap-2"
            size="sm"
          >
            <ChevronLeft className="h-4 w-4" />
            Previous
          </Button>
          
          <Button variant="outline" onClick={onCancel} size="sm">
            Cancel
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default SkillAssessmentQuestionnaire;
